import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import { FaPlus, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGuard from '../PermissionGuard';

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { checkPermission } = useAuth();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [masterDataList, setMasterDataList] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [masterDataSearch, setMasterDataSearch] = useState('');
  const [showMasterDataDropdown, setShowMasterDataDropdown] = useState(false);
  const [filteredMasterData, setFilteredMasterData] = useState([]);

  const [formData, setFormData] = useState({
    type: 'invoice',
    taskId: '',
    billingAddress: {
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      phone: '',
      email: ''
    },
    items: [{
      product: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      amount: 0,
      taxAmount: 0,
      totalAmount: 0
    }],
    dueDate: '',
    notes: '',
    terms: ''
  });

  useEffect(() => {
    fetchInitialData();
    if (isEditMode && id) {
      fetchInvoice();
    }
  }, [isEditMode, id]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMasterDataDropdown && !event.target.closest('.master-data-search')) {
        setShowMasterDataDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMasterDataDropdown]);

  const fetchInitialData = async () => {
    try {
      const [productsRes, masterDataRes, tasksRes] = await Promise.all([
        api.get('/invoices/products/available'),
        api.get('/master-data'),
        api.get('/tasks')
      ]);
      
      setProducts(productsRes.data);
      setMasterDataList(masterDataRes.data.data);
      setTasks(tasksRes.data);
    } catch (error) {
      toast.error('Failed to fetch required data');
      console.error('Error fetching data:', error);
    }
  };

  const handleMasterDataSearch = (searchTerm) => {
    setMasterDataSearch(searchTerm);
    if (searchTerm.trim() === '') {
      setFilteredMasterData([]);
      setShowMasterDataDropdown(false);
    } else {
      const filtered = masterDataList.filter(md => 
        md.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        md.clientEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        md.clientPhone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMasterData(filtered);
      setShowMasterDataDropdown(true);
    }
  };

  const selectMasterData = (masterData) => {
    setMasterDataSearch(masterData.clientName);
    setShowMasterDataDropdown(false);
    setFilteredMasterData([]);
    
    // Auto-fill billing address
    setFormData(prev => ({
      ...prev,
      billingAddress: {
        name: masterData.clientName || '',
        address: masterData.clientAddress || '',
        city: masterData.clientCity || '',
        state: masterData.clientState || '',
        zipCode: masterData.clientZipCode || '',
        country: masterData.clientCountry || '',
        phone: masterData.clientPhone || '',
        email: masterData.clientEmail || ''
      }
    }));
  };

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/invoices/${id}`);
      const invoice = response.data;
      
      setFormData({
        type: invoice.type || 'invoice',
        taskId: invoice.task?._id || '',
        billingAddress: {
          name: invoice.billingAddress?.name || '',
          address: invoice.billingAddress?.address || '',
          city: invoice.billingAddress?.city || '',
          state: invoice.billingAddress?.state || '',
          zipCode: invoice.billingAddress?.zipCode || '',
          country: invoice.billingAddress?.country || '',
          phone: invoice.billingAddress?.phone || '',
          email: invoice.billingAddress?.email || ''
        },
        items: invoice.items || [{
          product: '',
          description: '',
          quantity: 1,
          unitPrice: 0,
          taxRate: 0,
          amount: 0,
          taxAmount: 0,
          totalAmount: 0
        }],
        dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : '',
        notes: invoice.notes || '',
        terms: invoice.terms || ''
      });
    } catch (error) {
      toast.error('Failed to fetch invoice');
      console.error('Error fetching invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBillingAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        [field]: value
      }
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };

      // Recalculate amounts
      const item = newItems[index];
      item.amount = item.quantity * item.unitPrice;
      item.taxAmount = item.amount * (item.taxRate / 100);
      item.totalAmount = item.amount + item.taxAmount;

      return {
        ...prev,
        items: newItems
      };
    });
  };

  const handleProductSelect = async (index, productId) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      
      if (productId) {
        // Product is selected - auto-fill from product data
        const product = products.find(p => p._id === productId);
        if (product) {
          newItems[index] = {
            ...newItems[index],
            product: productId,
            description: product.name,
            unitPrice: product.fees?.professionalFees?.amount || 0,
            taxRate: product.fees?.professionalFees?.taxRate || 0
          };
        }
      } else {
        // No product selected - clear product reference but keep manual inputs
        newItems[index] = {
          ...newItems[index],
          product: null,
          // Keep existing description, unitPrice, and taxRate as they may have been manually entered
        };
      }

      // Recalculate amounts
      const item = newItems[index];
      item.amount = item.quantity * item.unitPrice;
      item.taxAmount = item.amount * (item.taxRate / 100);
      item.totalAmount = item.amount + item.taxAmount;

      return {
        ...prev,
        items: newItems
      };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product: '',
          description: '',
          quantity: 1,
          unitPrice: 0,
          taxRate: 0,
          amount: 0,
          taxAmount: 0,
          totalAmount: 0
        }
      ]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleBillingAddressAutoFill = async (source, id) => {
    try {
      const response = await api.get(`/invoices/billing-address/suggestions?source=${source}&id=${id}`);
      if (response.data.billingAddress) {
        setFormData(prev => ({
          ...prev,
          billingAddress: response.data.billingAddress
        }));
      }
    } catch (error) {
      toast.error('Failed to auto-fill billing address');
    }
  };

  const calculateTotals = () => {
    const subtotal = (formData.items || []).reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxTotal = (formData.items || []).reduce((sum, item) => sum + (item.taxAmount || 0), 0);
    const total = subtotal + taxTotal;
    return { subtotal, taxTotal, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.items || formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    if (!formData.billingAddress?.name || !formData.billingAddress?.address) {
      toast.error('Please fill in billing address');
      return;
    }

    try {
      setSubmitting(true);
      
      // Clean up items data - convert empty strings to null for product field
      const cleanedItems = formData.items.map(item => ({
        ...item,
        product: item.product || null
      }));
      
      const payload = {
        ...formData,
        items: cleanedItems,
        taskId: formData.taskId || undefined
      };

      if (isEditMode) {
        await api.put(`/invoices/${id}`, payload);
        toast.success('Invoice updated successfully');
      } else {
        await api.post('/invoices', payload);
        toast.success('Invoice created successfully');
      }
      
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const { subtotal, taxTotal, total } = calculateTotals();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
        </h1>
        <button
          onClick={() => navigate('/invoices')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <FaTimes className="mr-2" />
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice Type and Task */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="invoice">Invoice</option>
                <option value="proforma">Proforma Invoice</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task (Optional)</label>
              <select
                name="taskId"
                value={formData.taskId}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Task</option>
                {tasks.map(task => (
                  <option key={task._id} value={task._id}>
                    {task.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Issue Date</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* Billing Address */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Billing Address</h2>
            <div className="flex space-x-2">
              <div className="relative master-data-search">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Master Data</label>
                <input
                  type="text"
                  value={masterDataSearch}
                  onChange={(e) => handleMasterDataSearch(e.target.value)}
                  placeholder="Search by name, email, or phone..."
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                  onFocus={() => {
                    if (masterDataSearch.trim() !== '') {
                      setShowMasterDataDropdown(true);
                    }
                  }}
                />
                {showMasterDataDropdown && filteredMasterData.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredMasterData.map((md) => (
                      <div
                        key={md._id}
                        onClick={() => selectMasterData(md)}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-sm">{md.clientName}</div>
                        {md.clientEmail && (
                          <div className="text-xs text-gray-600">{md.clientEmail}</div>
                        )}
                        {md.clientPhone && (
                          <div className="text-xs text-gray-600">{md.clientPhone}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setMasterDataSearch('');
                  setShowMasterDataDropdown(false);
                  setFilteredMasterData([]);
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.billingAddress?.name || ''}
                onChange={(e) => handleBillingAddressChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.billingAddress?.email || ''}
                onChange={(e) => handleBillingAddressChange('email', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
              <input
                type="text"
                value={formData.billingAddress?.address || ''}
                onChange={(e) => handleBillingAddressChange('address', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={formData.billingAddress?.city || ''}
                onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={formData.billingAddress?.state || ''}
                onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
              <input
                type="text"
                value={formData.billingAddress?.zipCode || ''}
                onChange={(e) => handleBillingAddressChange('zipCode', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={formData.billingAddress?.phone || ''}
                onChange={(e) => handleBillingAddressChange('phone', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <FaPlus className="mr-2" />
              Add Item
            </button>
          </div>
          
          <div className="space-y-4">
            {(formData.items || []).map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                    <select
                      value={item.product || ''}
                      onChange={(e) => handleProductSelect(index, e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Custom Product (Manual Entry)</option>
                      {products.map(product => (
                        <option key={product._id} value={product._id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                    <input
                      type="text"
                      value={item.description || ''}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={item.product ? "Product name (editable)" : "Enter product name"}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={item.quantity || 0}
                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice || 0}
                      onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={item.taxRate || 0}
                      onChange={(e) => handleItemChange(index, 'taxRate', parseFloat(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="w-full px-3 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                
                <div className="mt-2 text-sm text-gray-600">
                  Amount: ${(item.amount || 0).toFixed(2)} | Tax: ${(item.taxAmount || 0).toFixed(2)} | Total: ${(item.totalAmount || 0).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax:</span>
                <span className="font-medium">${taxTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold border-t pt-2">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes and Terms */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleInputChange}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Payment terms and conditions..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <FaSave className="mr-2" />
            {submitting ? 'Saving...' : (isEditMode ? 'Update Invoice' : 'Create Invoice')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm; 